import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Play, AlertTriangle } from 'lucide-react';
const SEVERITY_COLORS = {
    critical: '#ff4d4f',
    high: '#ff7a45',
    medium: '#faad14',
    low: '#52c41a',
    info: '#1890ff',
    major: '#ff7a45',
    minor: '#faad14',
    warning: '#faad14',
    compliant: '#52c41a',
    violation: '#ff4d4f',
    undetermined: '#8c8c8c',
};
const TIMESTAMP_RE = /\[(\d+(?:\.\d+)?)s\s*[-\u2013]\s*(\d+(?:\.\d+)?)s\]/g;
// Keys that come from the raw LVS / OpenAI envelope and should never be shown
// to the user. They duplicate or wrap the actual report content.
const NOISE_KEYS = new Set([
    'raw',
    'choices',
    'tool_calls',
    'usage',
    'media_info',
    'object',
    'model',
    'created',
    'finish_reason',
    'index',
    'role',
    'id',
    'video_id',
    'uuid',
    'post_process_error',
    'lvs_response',
    'aggregation_latency',
    'summary_latency',
    'query_processing_time',
    'total_chunks_processed',
    'aggregation_tokens',
    'summary_tokens',
    'summary_requests',
    'start_offset',
    'end_offset',
]);
function stripNoise(value) {
    if (Array.isArray(value))
        return value.map(stripNoise);
    if (value && typeof value === 'object') {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            if (NOISE_KEYS.has(k.toLowerCase()))
                continue;
            out[k] = stripNoise(v);
        }
        return out;
    }
    return value;
}
// If a value is a string that contains JSON, try to parse it. Used to unwrap
// the LVS envelope's `choices[0].message.content` which is a JSON-encoded
// string of the actual structured report.
function tryUnwrapJsonString(value) {
    if (typeof value !== 'string')
        return value;
    const t = value.trim();
    if (!((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))))
        return value;
    try {
        return JSON.parse(t);
    }
    catch {
        return value;
    }
}
function humanizeKey(key) {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
function isCriticalFlag(key, value) {
    return key.toLowerCase() === 'critical' && String(value).toLowerCase() === 'true';
}
function severityColor(value) {
    return SEVERITY_COLORS[value.trim().toLowerCase()];
}
function TextWithTimestamps({ text, onSeek }) {
    const parts = [];
    let last = 0;
    const re = new RegExp(TIMESTAMP_RE.source, 'g');
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last)
            parts.push(text.slice(last, m.index));
        const start = parseFloat(m[1] ?? '0');
        parts.push(_jsxs("button", { type: "button", onClick: () => onSeek?.(start), title: `Jump to ${m[0]}`, style: {
                background: 'var(--ey-darker)',
                border: '1px solid var(--ey-border)',
                borderRadius: 4,
                padding: '0 6px',
                margin: '0 2px',
                fontSize: 12,
                cursor: onSeek ? 'pointer' : 'default',
                color: 'var(--ey-accent, #ffe600)',
            }, children: [_jsx(Play, { size: 10, style: { verticalAlign: 'middle', marginRight: 2 } }), m[0]] }, `${m.index}-${start}`));
        last = m.index + m[0].length;
    }
    if (last < text.length)
        parts.push(text.slice(last));
    return _jsx(_Fragment, { children: parts });
}
function Badge({ label, color }) {
    return (_jsx("span", { style: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            background: color ?? 'var(--ey-darker)',
            color: color ? '#000' : 'var(--ey-text)',
            border: '1px solid var(--ey-border)',
        }, children: label }));
}
function renderValue(_key, value, onSeek) {
    if (value === null || value === undefined || value === '') {
        return _jsx("span", { style: { color: 'var(--ey-muted)', fontStyle: 'italic' }, children: "\u2014" });
    }
    if (typeof value === 'boolean') {
        return _jsx(Badge, { label: value ? 'YES' : 'NO', color: value ? '#ff7a45' : undefined });
    }
    if (typeof value === 'number') {
        return _jsx("span", { style: { fontSize: 18, fontWeight: 600 }, children: value });
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        // Severity-like token
        if (/^(critical|high|medium|low|info|major|minor|warning|compliant|violation|undetermined|true|false|yes|no)$/i.test(trimmed)) {
            return _jsx(Badge, { label: trimmed.toUpperCase(), color: severityColor(trimmed) });
        }
        // Likely embedded JSON the model returned as a string (older personas)
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
                return _jsx(ObjectPanel, { data: JSON.parse(trimmed), onSeek: onSeek });
            }
            catch { /* fall through */ }
        }
        return (_jsx("span", { style: { whiteSpace: 'pre-wrap' }, children: _jsx(TextWithTimestamps, { text: value, onSeek: onSeek }) }));
    }
    if (Array.isArray(value)) {
        if (value.length === 0)
            return _jsx("span", { style: { color: 'var(--ey-muted)' }, children: "[]" });
        if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
            return (_jsx("ul", { style: { margin: 0, paddingLeft: 18 }, children: value.map((v, i) => (_jsx("li", { children: _jsx(TextWithTimestamps, { text: String(v), onSeek: onSeek }) }, i))) }));
        }
        return (_jsx("div", { style: { display: 'grid', gap: 8 }, children: value.map((v, i) => (_jsx("div", { style: {
                    padding: 8,
                    background: 'var(--ey-darker)',
                    borderRadius: 6,
                    border: '1px solid var(--ey-border)',
                }, children: _jsx(ObjectPanel, { data: v, onSeek: onSeek }) }, i))) }));
    }
    if (typeof value === 'object') {
        return _jsx(ObjectPanel, { data: value, onSeek: onSeek });
    }
    return _jsx("span", { children: String(value) });
}
function ObjectPanel({ data, onSeek }) {
    if (data === null || typeof data !== 'object') {
        return _jsx(_Fragment, { children: renderValue('', data, onSeek) });
    }
    const entries = Object.entries(data);
    return (_jsx("div", { style: { display: 'grid', gap: 10 }, children: entries.map(([k, v]) => {
            const isLong = (typeof v === 'string' && v.length > 80) ||
                (typeof v === 'object' && v !== null);
            return (_jsxs("div", { style: {
                    display: isLong ? 'block' : 'grid',
                    gridTemplateColumns: isLong ? undefined : 'minmax(120px, 180px) 1fr',
                    gap: 8,
                    alignItems: 'start',
                }, children: [_jsx("div", { style: {
                            fontSize: 11,
                            color: 'var(--ey-muted)',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            marginBottom: isLong ? 4 : 0,
                        }, children: humanizeKey(k) }), _jsx("div", { style: { minWidth: 0 }, children: renderValue(k, v, onSeek) })] }, k));
        }) }));
}
export function SchemaReportRenderer({ data, onSeek }) {
    if (data === null || data === undefined) {
        return _jsx("p", { style: { color: 'var(--ey-muted)' }, children: "No report data." });
    }
    if (typeof data !== 'object') {
        // Fallback: if it's actually a JSON-encoded report, unwrap it.
        const unwrapped = tryUnwrapJsonString(data);
        if (unwrapped !== data && unwrapped && typeof unwrapped === 'object') {
            return _jsx(SchemaReportRenderer, { data: unwrapped, onSeek: onSeek });
        }
        return _jsx("p", { style: { whiteSpace: 'pre-wrap' }, children: String(data) });
    }
    // Some legacy jobs store the raw LVS envelope under `report`. If the
    // top-level object looks like an OpenAI envelope (has `choices`) or wraps
    // the real report in a JSON-encoded `summary`/`raw` field, unwrap it.
    let working = data;
    const rawSummary = working['summary'];
    if (typeof rawSummary === 'string') {
        const unwrapped = tryUnwrapJsonString(rawSummary);
        if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
            // Extract the inner content if it's an OpenAI envelope.
            const env = unwrapped;
            const choices = env['choices'];
            if (Array.isArray(choices) && choices[0] && typeof choices[0] === 'object') {
                const msg = choices[0]['message'];
                const content = msg && typeof msg === 'object' ? msg['content'] : null;
                const inner = tryUnwrapJsonString(content);
                if (inner && typeof inner === 'object')
                    working = inner;
            }
            else {
                working = env;
            }
        }
    }
    const cleaned = stripNoise(working);
    const obj = cleaned;
    const critical = Object.entries(obj).find(([k, v]) => isCriticalFlag(k, v));
    const reasoning = obj['Reasoning'] ?? obj['reasoning'];
    return (_jsxs("div", { className: "card", style: { background: 'var(--ey-darker)', display: 'grid', gap: 10 }, children: [critical && (_jsxs("div", { style: {
                    background: '#ff4d4f',
                    color: '#fff',
                    padding: '8px 12px',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontWeight: 600,
                }, children: [_jsx(AlertTriangle, { size: 16 }), " CRITICAL", typeof reasoning === 'string' && (_jsxs("span", { style: { fontWeight: 400, fontSize: 13 }, children: ["\u2014 ", reasoning] }))] })), _jsx(ObjectPanel, { data: obj, onSeek: onSeek })] }));
}
